import React, { useState, useEffect } from 'react';
import { parquetRead, parquetMetadata } from 'hyparquet';
import { compressors } from 'hyparquet-compressors';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Search, Target, CheckCircle, Clock, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { fetchFileAsArrayBuffer } from '@/utils/fileUtils';

const ROWS_PER_PAGE = 50;

// Helper to check if column is an ACT score column
const isACTScoreColumn = (columnName) => {
  return columnName.endsWith('_accuracy_score') ||
         columnName.endsWith('_completeness_score') ||
         columnName.endsWith('_timeliness_score') ||
         columnName.endsWith('_total_act_score') ||
         columnName.endsWith('_days_since_changed') ||
         columnName.startsWith('record_');
};

// Helper to get ACT score type
const getACTScoreType = (columnName) => {
  if (columnName.includes('accuracy')) return 'accuracy';
  if (columnName.includes('completeness')) return 'completeness';
  if (columnName.includes('timeliness')) return 'timeliness';
  if (columnName.includes('total_act')) return 'total';
  if (columnName.includes('days_since_changed')) return 'days';
  if (columnName.startsWith('record_')) return 'record';
  return null;
};

// Helper to get color for ACT score value
const getACTScoreColor = (columnName, value) => {
  const numValue = Number(value);
  if (isNaN(numValue)) return 'text-slate-600';

  if (columnName.includes('accuracy')) {
    return numValue === 1 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold';
  }

  if (columnName.includes('completeness')) {
    // 0-3 scale
    if (numValue >= 3) return 'text-green-600 font-semibold';
    if (numValue >= 2) return 'text-amber-600';
    return 'text-red-600';
  }

  if (columnName.includes('timeliness')) {
    // 0-4 scale
    if (numValue >= 3) return 'text-green-600 font-semibold';
    if (numValue >= 2) return 'text-amber-600';
    return 'text-red-600';
  }

  if (columnName.includes('total_act')) {
    // 0-8 scale
    if (numValue >= 6) return 'text-green-600 font-semibold';
    if (numValue >= 4) return 'text-amber-600';
    return 'text-red-600';
  }

  if (columnName.includes('days_since_changed')) {
    if (numValue < 4) return 'text-green-600';
    if (numValue < 8) return 'text-amber-600';
    return 'text-slate-600';
  }

  return 'text-slate-600';
};

// Helper to get icon for ACT score column
const getACTScoreIcon = (type) => {
  switch (type) {
    case 'accuracy': return <Target className="h-3 w-3 inline mr-1" />;
    case 'completeness': return <CheckCircle className="h-3 w-3 inline mr-1" />;
    case 'timeliness': return <Clock className="h-3 w-3 inline mr-1" />;
    case 'total': return <TrendingUp className="h-3 w-3 inline mr-1" />;
    case 'days': return <Clock className="h-3 w-3 inline mr-1" />;
    default: return null;
  }
};

export default function ParquetViewer({ open, onOpenChange, fileKey, fileName, bucket }) {
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (open && fileKey) {
      loadParquetData();
    }
  }, [open, fileKey]);

  const loadParquetData = async () => {
    setLoading(true);
    try {
      // Use utility function to fetch file (same as download)
      const arrayBuffer = await fetchFileAsArrayBuffer(fileKey, bucket);

      // Try to get metadata to extract column names
      let columnNamesFromMetadata = null;
      try {
        const metadata = parquetMetadata(arrayBuffer);
        console.log('Parquet metadata:', metadata);

        // Try different ways to get column names from schema
        if (metadata.schema) {
          // Try to get leaf columns
          const leafColumns = metadata.schema.filter(col => col.element);
          if (leafColumns.length > 0) {
            // Filter out Spark metadata columns
            columnNamesFromMetadata = leafColumns
              .map(col => col.element.name)
              .filter(name => name !== 'spark_schema' && !name.startsWith('_spark_'));
          } else {
            // Fallback: get all column names, filtering out schema/spark metadata
            columnNamesFromMetadata = metadata.schema
              .filter(col => col.name && col.name !== 'schema' && col.name !== 'spark_schema' && !col.name.startsWith('_spark_'))
              .map(col => col.name);
          }
        }
        console.log('Column names from metadata:', columnNamesFromMetadata);
      } catch (metaError) {
        console.warn('Could not extract metadata:', metaError);
      }

      // Parse with hyparquet
      parquetRead({
        file: arrayBuffer,
        compressors,
        onComplete: (data) => {
          console.log('Parquet data received:', data.length, 'rows');
          console.log('First row sample:', data[0]);
          console.log('First row is array?', Array.isArray(data[0]));

          // Check if data is array of arrays or array of objects
          if (data && data.length > 0) {
            if (Array.isArray(data[0])) {
              // Data is array of arrays
              const columnNames = columnNamesFromMetadata ||
                Array.from({ length: data[0].length }, (_, i) => `Column ${i + 1}`);

              // Convert arrays to objects
              const objectData = data.map(row => {
                const obj = {};
                columnNames.forEach((col, idx) => {
                  obj[col] = row[idx];
                });
                return obj;
              });

              setColumns(columnNames);
              setData(objectData);
            } else {
              // Data is already array of objects
              const actualColumns = Object.keys(data[0]);
              setColumns(actualColumns);
              setData(data);
            }
          } else {
            setColumns([]);
            setData([]);
          }
          setLoading(false);
        },
      });
    } catch (error) {
      console.error('Error loading parquet file:', error);
      toast.error(`Failed to load parquet file: ${error.message}`);
      setLoading(false);
    }
  };

  // Filter data based on search query
  const filteredData = searchQuery
    ? data.filter((row) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : data;

  // Paginate the filtered data
  const totalPages = Math.ceil(filteredData.length / ROWS_PER_PAGE);
  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const endIndex = startIndex + ROWS_PER_PAGE;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-[95vw] w-[95vw] h-[90vh] overflow-hidden flex flex-col p-6">
        <DialogHeader>
          <DialogTitle>View Parquet File: {fileName}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="space-y-3 flex-1 overflow-auto">
            <Skeleton className="h-10 w-full" />
            {[...Array(10)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <>
            {/* Search and Info Bar */}
            <div className="flex items-center gap-4 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search in table..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="pl-10"
                />
              </div>
              <div className="text-sm text-slate-600">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredData.length)} of{' '}
                {filteredData.length} rows
                {searchQuery && ` (filtered from ${data.length} total)`}
              </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto border rounded-lg">
              {paginatedData.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  {searchQuery ? 'No rows match your search' : 'No data available'}
                </div>
              ) : (
                <Table>
                  <TableHeader className="sticky top-0 bg-card-bg z-10">
                    <TableRow>
                      {columns.map((column) => {
                        const isACTCol = isACTScoreColumn(column);
                        const actType = isACTCol ? getACTScoreType(column) : null;
                        return (
                          <TableHead
                            key={column}
                            className={`font-semibold ${isACTCol ? 'bg-blue-50' : ''}`}
                          >
                            {isACTCol && getACTScoreIcon(actType)}
                            {column}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedData.map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {columns.map((column) => {
                          const isACTCol = isACTScoreColumn(column);
                          const value = row[column];
                          const colorClass = isACTCol ? getACTScoreColor(column, value) : 'text-slate-600';

                          return (
                            <TableCell
                              key={column}
                              className={`whitespace-nowrap ${isACTCol ? 'bg-blue-50/30' : ''} ${colorClass}`}
                            >
                              {value != null ? String(value) : ''}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-slate-600">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
